import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TagBloc } from '@kbloc';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { AuthDataService, DataStoreService, productDisplayName, TitleService } from '@kservice';
import { Credentials, DataStatus, Status } from '@ktypes/models';
import { validateKeyIsNumber } from '@kutil';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kp-password-reset',
  templateUrl: './password-reset.component.html',
  styleUrls: ['./password-reset.component.scss'],
})
export class PasswordResetComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();
  private _title = 'Reset Password';

  resetForm: UntypedFormGroup;
  passwordCode: UntypedFormControl;
  newPassword: UntypedFormControl;
  passwordValidationCopy = 'Password must be at least 8 characters and contain at least 1 number and 1 letter.';
  codeMismatchError = this.authenticationBloc.CODE_MISMATCH_ERROR;
  showForm = true;
  hasError = false;
  showError = false;
  showCodeError = false;
  hasInvalidPasswordError = false;
  hasInvalidResetCodeError = false;
  showPasswordSuccess = false;
  processing = false;
  routeToInitialAfterLogin = false;
  routeToUrl: string;
  productName = productDisplayName;
  responseErrorMessage: string;
  email: string;
  displayErrorMessage: string;
  showPassword = false;

  constructor(
    private _authDataService: AuthDataService,
    public authenticationBloc: AuthenticationBloc,
    private _dataStoreService: DataStoreService,
    private _router: Router,
    private _tagBloc: TagBloc,
    private _titleService: TitleService
  ) {}

  ngOnInit() {
    this._titleService.set(this._title);
    this.passwordCode = new UntypedFormControl(this.passwordCode, [Validators.required, Validators.pattern(/^[\S]+$/)]);
    this.routeToUrl = this.authenticationBloc.changePasswordReturnUrl || '/login/reset/code';
    this.newPassword = new UntypedFormControl(this.newPassword, [
      Validators.required,
      Validators.pattern(/^.{8,}$/),
      Validators.pattern(/[A-Za-z]/),
      Validators.pattern(/\d/),
      Validators.pattern(/^[\S]+.*[\S]+$/),
    ]);
    this.resetForm = new UntypedFormGroup({
      passwordCode: this.passwordCode,
      newPassword: this.newPassword,
    });
    this.email = this.authenticationBloc.resetEmail || this._authDataService.loadToken()?.user?.email;
    this._dataStoreService.authStatus$.pipe(takeUntil(this._destroy$)).subscribe((loginStatus) => {
      if (
        loginStatus &&
        loginStatus.status === Status.done &&
        loginStatus.message.code === 200 &&
        this.routeToInitialAfterLogin
      ) {
        this._tagBloc.getUserTags();
        void this._router.navigate(['']);
      }
    });
    this.updateDisplayErrorMessage();
    this.authenticationBloc.resetPasswordStatus$.pipe(takeUntil(this._destroy$)).subscribe((resetPasswordStatus) => {
      if (resetPasswordStatus?.status === Status.done) {
        this.showPasswordSuccess = true;
        this.showForm = false;
        this.processing = false;
        this.hasError = false;
        this.hasInvalidPasswordError = false;
        this.hasInvalidResetCodeError = false;
        this.showError = false;
      } else if (resetPasswordStatus?.status === Status.error) {
        this.hasInvalidPasswordError =
          resetPasswordStatus.message.code === 403 &&
          resetPasswordStatus.message.message === 'InvalidPasswordException';
        this.hasInvalidResetCodeError =
          resetPasswordStatus.message.code === 403 && resetPasswordStatus.message.message === 'CodeMismatchException';
        this.hasError = !this.hasInvalidPasswordError && !this.hasInvalidResetCodeError;
        this.showError = this.hasError;
        this.showForm = !this.showError;
        this.processing = false;
        console.warn('authenticationBloc changePassword error', resetPasswordStatus.status);
      }

      this.updateResponseErrorMessage(resetPasswordStatus);
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  updateResponseErrorMessage(status: DataStatus<boolean>): void {
    this.showCodeError = status?.message?.message === 'CodeMismatchException';
    this.responseErrorMessage = this.authenticationBloc.getAuthErrorMessage(status?.message?.message);
    this.updateDisplayErrorMessage();
  }

  updateDisplayErrorMessage() {
    // code mismatch error is displayed below the code field
    if (this.responseErrorMessage && this.responseErrorMessage !== this.codeMismatchError) {
      this.displayErrorMessage = this.responseErrorMessage;
    } else if (!this.newPassword.valid) {
      this.displayErrorMessage = this.passwordValidationCopy;
    } else {
      this.displayErrorMessage = null;
    }
  }

  onSubmit() {
    this.processing = true;
    this.authenticationBloc.changePassword(this.passwordCode.value, this.newPassword.value, this._submitError);
  }

  private _submitError(): void {
    this.hasError = true;
    this.showError = true;
    this.showForm = false;
    this.processing = false;
  }

  resetState() {
    this.hasError = false;
    this.showError = false;
    this.showPasswordSuccess = false;
    this.showForm = true;
    this.processing = false;
  }

  login() {
    this.authenticationBloc.resetPaymentRequiredErrorMessage();
    if (this.authenticationBloc.changePasswordReturnUrl != null || this.authenticationBloc.isLoggedIn()) {
      this.routeToInitialAfterLogin = this.authenticationBloc.changePasswordReturnUrl != null;
      void this._router.navigate([this.authenticationBloc.changePasswordReturnUrl ?? '/settings']);
      this.authenticationBloc.setChangePasswordReturnUrl(null);
    } else {
      this.authenticationBloc.login(new Credentials(this.authenticationBloc.resetEmail, this.newPassword.value), () =>
        this._loginError()
      );
      this.routeToInitialAfterLogin = true;
    }
    this.passwordCode.setValue('');
    this.newPassword.setValue('');
  }

  passwordChanged() {
    this.updateDisplayErrorMessage();
  }

  toggleShowPassword(event: MouseEvent) {
    // Make sure the button won't submit the form
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.showPassword = !this.showPassword;
  }

  handleKeydown($event: KeyboardEvent) {
    // reset error state if they are "true"
    if (this.hasError || this.showError) {
      this.hasError = false;
      this.showError = false;
    }
    if (!validateKeyIsNumber($event)) {
      $event.preventDefault();
      return false;
    }
  }

  private _loginError(): void {
    console.warn('Error logging in with new password');
    this.hasError = true;
    this.showForm = false;
    this.processing = false;
  }
}
